import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Classes, Tooltip } from '@blueprintjs/core';
import { classNames } from '@axo/shared/util/dom';
import { useApplicationDetail } from '@axo/shared/data-access/hooks';
import { IconButton } from '../IconButton';
import { useLockAndOpenApplication } from '../../hooks/utils/useLockAndOpenApplication';
import styles from './ApplicationLink.module.css';

export function ApplicationLink({
  id,
  humanReadableID,
  marketCountry,
  children,
  className,
}: {
  id: string;
  humanReadableID?: string;
  marketCountry?: string;
  children?: ReactNode;
  className?: string;
}) {
  // Should only be looked up when human readable id or market is missing.
  const app = useApplicationDetail(
    resolveLookupID(id, humanReadableID, marketCountry),
  );

  const realMarketCountry = app.data?.MarketCountry ?? marketCountry;
  const realHRID = app.data?.HumanReadableID ?? humanReadableID;

  const openApplication = useLockAndOpenApplication(realMarketCountry ?? '');

  return (
    <div
      className={classNames(
        styles.linkContainer,
        className,
        app.isLoading && Classes.SKELETON,
      )}
    >
      <Link to={`/application/${id}`}>{children ?? realHRID ?? id}</Link>

      <Tooltip content="Open in background tab">
        <IconButton
          icon="share"
          classes={{ icon: styles.icon }}
          onClick={() =>
            realHRID &&
            openApplication({
              HumanReadableID: realHRID,
              LoanApplicationID: id,
            })
          }
          iconPlacement="right"
        />
      </Tooltip>
    </div>
  );
}

// This is the most hacky solution I was able to come up with to only fetch
// application details when they are actually needed. Because the hook will
// not enable whenever loan application id is undefined, we take advantage of
// this to make sure we only look up the application in cases where either the
// humanReadableID or marketCountry is missing.
function resolveLookupID(
  id: string,
  humanReadableID: string | undefined,
  marketCountry: string | undefined,
) {
  if (humanReadableID === undefined || marketCountry === undefined) {
    return id;
  }

  return undefined;
}
